import { NasLogo, NasMark } from "../../components/brand/NasLogo";
import { Wordmark } from "../headliner/brands";
import "../../components/brand/nas-logo.css";
import "./brand.css";

/* Brand identity — forslag v1 til Apriil. Ett ark: merke, lockups, elementer, palett og bruk. Ingen uppercase-titler; tracked labels kun som metadata. */
const PALETTE = [
  ["Charcoal", "#111111", "Primary ink · dark surfaces"],
  ["Paper", "#F6F3EB", "Primary surface · light decks"],
  ["Sand", "#D9D4C8", "Secondary surface · hairlines"],
  ["Nordic Blue", "#62738A", "Editorial accent · data, charts"],
  ["Signal", "#F1E75A", "One highlight per slide · the headliner moments"],
];

const Sec = ({ nr, title, children, className = "" }) => (
  <section className={`br-sec ${className}`} data-testid={`br-sec-${nr}`}>
    <p className="br-lab"><span>{nr}</span>{title}</p>
    {children}
  </section>
);

export default function BrandSheet() {
  return (
    <main className="br-sheet" data-testid="brand-sheet">
      <header className="br-head">
        <p className="br-id"><b>Norwegian AI Summit</b><span>Brand identity · proposal v1 · for Apriil to develop</span></p>
        <p className="br-meta">Bergen · 13 April 2027 · The Next Shift</p>
      </header>

      <div className="br-hero">
        <div>
          <h1 data-testid="br-hero-h">A room for the people<br />deciding what comes next.</h1>
          <p className="br-sub">Norwegian AI Summit brings leadership, capital, technology and policy into one room — one day a year, in Bergen. The identity should read as an institution from the first announcement: sharp, quiet, editorial.</p>
        </div>
        <ul className="br-facts">
          <li><span>Place</span>Bergen, Norway</li>
          <li><span>Date</span>13 April 2027</li>
          <li><span>Theme 2027</span>The Next Shift</li>
          <li><span>Typeface</span>Geist · 500 / 450 / 400</li>
        </ul>
      </div>

      <div className="br-row br-row-3">
        <Sec nr="01" title="Primary logo" className="br-primary">
          <NasLogo size={56} testid="br-logo-primary" />
          <p className="br-note">Symbol + wordmark, one line. Optical alignment: the mark sits on the baseline of the wordmark; gap = 0.42 × cap height. Minimum height 18 px / 6 mm.</p>
        </Sec>
        <Sec nr="02" title="Stacked logo">
          <NasLogo stacked size={44} theme testid="br-logo-stacked" />
          <p className="br-note">For square and portrait formats — social, badges, the cover of a printed programme. The theme line is optional and changes every year.</p>
        </Sec>
        <Sec nr="03" title="Symbol">
          <NasMark size={132} />
          <p className="br-note"><b>The shift.</b> Three identical bars, each moved one level up. Same room, next level — the theme drawn literally, without an arrow, a brain or a circuit. Works at 16 px.</p>
        </Sec>
      </div>

      <div className="br-row br-row-3b">
        <Sec nr="04" title="Alternative mark — the rising N">
          <div className="br-alt">
            <NasMark variant="n" size={132} />
            <NasLogo variant="n" size={40} testid="br-logo-n" />
          </div>
          <p className="br-note">A geometric N with vertical stems and a rising silhouette — Norwegian, and moving. More monogram, less diagram. Kept as an option if Apriil wants a letterform rather than a symbol.</p>
        </Sec>
        <Sec nr="05" title="Brand elements">
          <div className="br-elements">
            <div><NasMark size={64} /><span>Symbol</span></div>
            <div><span className="br-word">Norwegian AI Summit</span><span>Wordmark</span></div>
            <div><span className="br-theme">The Next Shift</span><span>Theme line · changes yearly</span></div>
          </div>
        </Sec>
        <Sec nr="06" title="Colour palette">
          <ul className="br-palette" data-testid="br-palette">
            {PALETTE.map(([n, hex, use]) => (
              <li key={hex}><i style={{ background: hex }} className={hex === "#F6F3EB" ? "line" : ""} /><b>{n}</b><code>{hex}</code><span>{use}</span></li>
            ))}
          </ul>
        </Sec>
      </div>

      <Sec nr="07" title="In use" className="br-use">
        <div className="br-use-grid">
          <div className="br-tile dark">
            <span className="br-tile-lab">Deck masthead · dark cover</span>
            <p className="br-lockup"><NasLogo size={22} /><i>×</i><Wordmark name="DNB" h={22} /></p>
            <span className="br-tile-right">Presenting Partnership · Private &amp; confidential</span>
          </div>
          <div className="br-tile light">
            <span className="br-tile-lab">Deck masthead · light slides</span>
            <p className="br-lockup"><NasLogo size={22} /></p>
            <span className="br-tile-right">The programme</span>
          </div>
          <div className="br-tile favicons">
            <span className="br-tile-lab">Favicon · app icon</span>
            <div className="br-fav">
              <span className="f16"><NasMark size={16} /></span>
              <span className="f32"><NasMark size={32} /></span>
              <span className="f64 dark"><NasMark size={40} /></span>
              <span className="f64 signal"><NasMark size={40} /></span>
            </div>
          </div>
          <div className="br-tile stacked-dark">
            <NasLogo stacked size={40} theme variant="shift" />
          </div>
        </div>
        <p className="br-note wide">Rules: the mark is always one colour. Charcoal on Paper; Paper on Charcoal; never on photography without a solid field. Signal yellow is used once per surface — for the headliner moment, never for the logo. No gradients, no outlines, no drop shadows.</p>
      </Sec>

      <footer className="br-foot">
        <span>Norwegian AI Summit · Bergen · 2027</span>
        <span>Proposal v1 — a starting point for Apriil's identity and launch system, not a finished brand</span>
        <span>The Next Shift</span>
      </footer>
    </main>
  );
}
